<template>
  <div class="conversational-agent-chat">
    <div
      ref="scrollArea"
      class="agent-messages border rounded bg-light px-2 py-2 mb-2"
      :style="{ maxHeight: maxHeight, overflowY: 'auto' }"
      role="log"
      aria-live="polite"
    >
      <p
        v-if="introText && displayMessages.length === 0 && !hasGreetingVariants"
        class=" text-muted mb-2"
      >
        {{ introText }}
      </p>
      <div
        v-for="(m, idx) in displayMessages"
        :key="idx"
        class="mb-3"
        :class="m.role === 'user' ? 'text-end' : 'text-start'"
      >
        <template v-if="m.role === 'user'">
          <div
            class="d-inline-block text-start rounded px-2 py-1  bg-info text-white"
            style="max-width: 95%; white-space: pre-wrap"
          >{{ m.content }}</div>
        </template>
        <template v-else>
          <div
            class="d-inline-block text-start rounded px-2 py-1 "
            :class="assistantBubbleClass"
            style="max-width: 95%; white-space: pre-wrap"
          >{{ m.content }}</div>
          <div
            v-if="tokenUsageLabel(m)"
            class="small text-muted mt-1"
            style="max-width: 95%"
          >
            <i class="fas fa-microchip me-1" aria-hidden="true"></i>{{ tokenUsageLabel(m) }}
          </div>
          <div
            v-if="assistantChoicesVisible(m, idx)"
            class="mt-2 ps-0 agent-choice-chips-wrap"
          >
            <div class="d-flex flex-wrap gap-2 align-items-center">
              <button
                v-for="c in m.choices"
                :key="c.value"
                type="button"
                class="btn btn-sm rounded-pill px-3 py-1 shadow-sm agent-choice-chip"
                :disabled="disableChoices || loading || busyOverlay"
                @click="onChoiceClick(idx, c)"
              >
                {{ c.label }}
              </button>
            </div>
          </div>
        </template>
      </div>
      <div v-if="loading || busyOverlay" class="small text-muted">
        <div class="d-flex align-items-start flex-wrap gap-2">
          <span class="spinner-border spinner-border-sm mt-1" role="status" />
          <span class="flex-grow-1" style="min-width: 120px">
            {{ loadingLabel }}
            <span class="agent-thinking-dots" aria-hidden="true">
              <span class="agent-thinking-dot">.</span>
              <span class="agent-thinking-dot">.</span>
              <span class="agent-thinking-dot">.</span>
            </span>
            <div
              v-if="progressText"
              class="mt-1 fst-italic text-secondary"
              style="white-space: pre-wrap; word-break: break-word"
            >
              {{ progressText }}
            </div>
            <div
              v-if="patienceHintLine"
              class="mt-2 small text-secondary border-start border-3 ps-2"
              style="border-color: rgba(108, 117, 125, 0.55) !important"
            >
              {{ patienceHintLine }}
            </div>
          </span>
          <button
            v-if="loading && useProgressStream && allowAbortWhileLoading"
            type="button"
            class="btn btn-outline-secondary btn-sm align-self-start text-nowrap"
            @click="abortActiveTurn"
          >
            {{ abortTurnLabel }}
          </button>
        </div>
      </div>
    </div>
    <div v-if="!hideComposer">
      <div
        v-if="allowFileAttachments && pendingAttachments.length"
        class="d-flex flex-wrap gap-1 mb-2"
      >
        <span
          v-for="(att, attIdx) in pendingAttachments"
          :key="att.fileKey || attIdx"
          class="badge bg-secondary text-white d-inline-flex align-items-center gap-1 py-2 px-2"
        >
          <i class="fas fa-paperclip" aria-hidden="true"></i>
          <span class="text-truncate" style="max-width: 160px">{{ att.fileName || att.fileKey }}</span>
          <button
            type="button"
            class="btn-close btn-close-white btn-sm ms-1"
            style="font-size: 0.55rem"
            :disabled="loading || busyOverlay || attachmentUploading"
            :aria-label="$tn('Remove')"
            @click="removePendingAttachment(attIdx)"
          ></button>
        </span>
      </div>
      <div
        class="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-end"
      >
        <div class="d-flex flex-grow-1 gap-1 align-items-end">
          <button
            v-if="allowFileAttachments"
            type="button"
            class="btn btn-outline-secondary btn-sm flex-shrink-0"
            :disabled="
              loading ||
              busyOverlay ||
              disabled ||
              attachmentUploading ||
              pendingAttachments.length >= maxPendingAttachments
            "
            :title="$tn('ai.supportChatAttach')"
            @click="triggerAttachmentPick"
          >
            <i class="fas fa-paperclip" aria-hidden="true"></i>
          </button>
          <textarea
            v-model="draft"
            class="form-control form-control-sm flex-grow-1"
            :rows="inputRows"
            :placeholder="placeholder"
            :disabled="loading || busyOverlay || disabled || attachmentUploading"
            @keydown.enter.exact.prevent="onEnterSend"
          />
        </div>
        <button
          type="button"
          class="btn btn-info btn-sm text-white text-nowrap"
          :disabled="loading || busyOverlay || disabled || attachmentUploading || !canSendTurn"
          @click="sendFromDraft"
        >
          {{ sendLabel }}
        </button>
      </div>
      <input
        v-if="allowFileAttachments"
        ref="attachmentInput"
        type="file"
        class="d-none"
        :accept="attachmentFileAccept"
        multiple
        @change="onAttachmentInputChange"
      />
    </div>
    <div class="d-flex flex-wrap gap-2 mt-2">
      <button
        type="button"
        class="btn btn-link btn-sm text-muted p-0"
        :disabled="loading || busyOverlay"
        @click="clear"
      >
        {{ clearLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import {
  postJurisprudenciaNdjson,
  formatJurisSearchProgress,
} from "@/utils/jurisprudenciaSearchStream";
import {
  postSupportAssistantNdjson,
  formatSupportAssistantProgress,
} from "@/utils/supportAssistantStream";
import {
  SUPPORT_ASSISTANT_FILE_ACCEPT,
  SUPPORT_ASSISTANT_MAX_FILE_BYTES,
  SUPPORT_ASSISTANT_MAX_PENDING_ATTACHMENTS,
  buildUserMessageWithAttachments,
  isSupportAssistantAttachmentAllowed,
  uploadAndPrepareSupportAssistantAttachment,
} from "@/utils/supportAssistantAttachments";
import { isUploadPayloadTooLarge, showSupportUpload413Dialog } from "@/utils/supportTicketUpload";

/** Mensaje técnico de axios cuando no hay respuesta del servidor. */
function isAxiosNetworkLikeError(err) {
  if (!err || err.response) return false;
  const code = String(err.code || "").toUpperCase();
  const m = String(err.message || "")
    .trim()
    .toLowerCase();
  if (code === "ERR_NETWORK") return true;
  if (m === "network error" || m === "error de red") return true;
  if (m.includes("failed to fetch")) return true;
  if (["ENOTFOUND", "ECONNREFUSED", "EAI_AGAIN"].includes(code)) return true;
  if (err.request && !err.response) return true;
  return false;
}

function isAxiosTimeoutError(err, rawMessage) {
  if (!err) return false;
  const raw = String(rawMessage || err.message || "");
  if (/timeout of \d+ms exceeded/i.test(raw)) return true;
  if (String(err.code || "") === "ECONNABORTED" && /timeout/i.test(raw))
    return true;
  return false;
}

export default {
  name: "ConversationalAgentChat",
  props: {
    /** Ruta API (ej. /api/jurisprudencia/agent-chat-turn). Vacío si solo usás modo local. */
    endpoint: {
      type: String,
      default: "",
    },
    introText: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    sendLabel: {
      type: String,
      default: "Enviar",
    },
    clearLabel: {
      type: String,
      default: "Reiniciar conversación",
    },
    loadingLabel: {
      type: String,
      default: "…",
    },
    maxHeight: {
      type: String,
      default: "280px",
    },
    inputRows: {
      type: Number,
      default: 2,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Timeout ms máximo de pared para cada turno */
    requestTimeoutMs: {
      type: Number,
      default: 120000,
    },
    /**
     * Sin actividad NDJSON durante este ms se aborta (solo si resetTimeoutOnStreamProgress).
     * 0 = mismo que requestTimeoutMs fijo desde el inicio.
     */
    requestIdleTimeoutMs: {
      type: Number,
      default: 0,
    },
    /** Reinicia el temporizador en cada línea de estado del stream (soporte / NDJSON). */
    resetTimeoutOnStreamProgress: {
      type: Boolean,
      default: false,
    },
    /** Recibe líneas de estado NDJSON junto al spinner (mismo endpoint jurisprudencia). */
    useProgressStream: {
      type: Boolean,
      default: true,
    },
    /** juris = búsqueda jurisprudencia/MEV; support = asistente de soporte ExportAI. */
    progressStreamKind: {
      type: String,
      default: "juris",
    },
    /** Aborta el fetch del turno (p. ej. búsqueda MEV multi-organismo). */
    allowAbortWhileLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Mientras el spinner está activo, rota mensajes (p. ej. SAIJ lento / análisis largo).
     * Requiere claves i18n jurisprudencia.saijPatienceHint1..7 y analyzePatienceHint1..6.
     */
    showPatienceHints: {
      type: Boolean,
      default: false,
    },
    patienceHintIntervalMs: {
      type: Number,
      default: 13000,
    },
    abortTurnLabel: {
      type: String,
      default: "Detener",
    },
    /** Prefijo i18n para pasos de progreso (ej. mev.progress para MEV). */
    progressKeyPrefix: {
      type: String,
      default: "jurisprudencia.progress",
    },
    /** Se fusiona en el body del POST (ej. jurisAgentSessionId). */
    requestBodyExtra: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Devuelve props extra por turno (p. ej. listingCapabilities solo si el texto del usuario lo amerita).
     * Se fusiona encima de requestBodyExtra.
     */
    buildRequestBodyExtra: {
      type: Function,
      default: null,
    },
    /** Frases iniciales del asistente (se elige una al azar al montar y tras «reiniciar»). */
    greetingVariants: {
      type: Array,
      default: () => [],
    },
    /** Clases CSS para la burbuja del asistente (Bootstrap). */
    assistantBubbleClass: {
      type: String,
      default: "bg-white border shadow-sm",
    },
    /**
     * Si es false, tras «Reiniciar» no se vuelve a elegir saludo local: el padre puede
     * pedir uno nuevo (p. ej. por API) y remontar el componente.
     */
    seedAfterClear: {
      type: Boolean,
      default: true,
    },
    /** No llama al servidor: solo agrega el mensaje del usuario y emite `local-submit`. */
    localMode: {
      type: Boolean,
      default: false,
    },
    /** Oculta caja de texto y botón Enviar (p. ej. flujo solo con opciones clicables). */
    hideComposer: {
      type: Boolean,
      default: false,
    },
    /** No inserta saludo inicial desde `greetingVariants` al montar. */
    skipSeedGreeting: {
      type: Boolean,
      default: false,
    },
    /** Deshabilita todas las opciones tipo chip (p. ej. mientras el padre consulta un API). */
    disableChoices: {
      type: Boolean,
      default: false,
    },
    /**
     * Si el último mensaje del usuario es solo saludo, responder con bienvenida completa
     * (sin llamar al modelo ni usar el historial como contexto de esa respuesta).
     */
    freshWelcomeOnUserGreeting: {
      type: Boolean,
      default: false,
    },
    /** (userText) => string | null — mensaje de bienvenida cuando freshWelcomeOnUserGreeting. */
    getFreshWelcomeMessage: {
      type: Function,
      default: null,
    },
    /** PDF, Excel, imágenes, TXT/CSV → S3 + vista previa para asistente de soporte. */
    allowFileAttachments: {
      type: Boolean,
      default: false,
    },
    maxPendingAttachments: {
      type: Number,
      default: SUPPORT_ASSISTANT_MAX_PENDING_ATTACHMENTS,
    },
    attachmentFileAccept: {
      type: String,
      default: SUPPORT_ASSISTANT_FILE_ACCEPT,
    },
  },
  data() {
    return {
      draft: "",
      pendingAttachments: [],
      attachmentUploading: false,
      /** Mensajes enviados al servidor (user + assistant) */
      messages: [],
      loading: false,
      /** Spinner local sin POST (p. ej. padre consulta otro API). */
      busyOverlay: false,
      lastError: null,
      progressText: "",
      /** Paso NDJSON actual (saij_search, analyzing_results, etc.). */
      progressStep: "",
      patienceHintIndex: 0,
      _patienceRotateTimer: null,
      /** true si el abort vino del timeout del turno (no del botón Detener). */
      _turnTimedOut: false,
      _activeTurnAbort: null,
      _streamTurnAbortTimer: null,
      /** Vista previa NDJSON recibida en el turno actual (no el saludo inicial). */
      _turnHadStreamPreview: false,
      /** Estado de puertas IA (reanudar flujo sin volver a la raíz). */
    };
  },
  computed: {
    displayMessages() {
      return this.messages;
    },
    hasGreetingVariants() {
      const g = this.greetingVariants;
      return Array.isArray(g) && g.some((s) => String(s || "").trim());
    },
    patienceHintCategory() {
      const s = String(this.progressStep || "");
      if (
        [
          "agent_thinking",
          "saij_search",
          "paginating_saij",
          "expanding_search",
          "queries_planned",
          "planning_queries",
          "rerun_search",
          "intent_ready",
          "interpreting_question",
        ].includes(s)
      ) {
        return "saij";
      }
      if (
        [
          "fetching_saij_doc_text",
          "analyzing_results",
          "analyzing_hits",
        ].includes(s)
      ) {
        return "analyze";
      }
      return "";
    },
    isAdminUser() {
      return localStorage.getItem("role") === "admin";
    },
    canSendTurn() {
      const hasText = !!(this.draft || "").trim();
      const hasFiles =
        this.allowFileAttachments && this.pendingAttachments.length > 0;
      return hasText || hasFiles;
    },
    patienceHintLine() {
      if (!this.loading || !this.showPatienceHints) {
        return "";
      }
      const cat = this.patienceHintCategory || "saij";
      const n = cat === "saij" ? 7 : 6;
      const base =
        cat === "saij"
          ? "jurisprudencia.saijPatienceHint"
          : "jurisprudencia.analyzePatienceHint";
      const i = (this.patienceHintIndex % n) + 1;
      const key = `${base}${i}`;
      return this.$te(key) ? String(this.$tn(key)) : "";
    },
  },
  watch: {
    loading(val) {
      if (val && this.showPatienceHints) {
        this.startPatienceRotation();
      } else {
        this.stopPatienceRotation();
      }
    },
    patienceHintCategory() {
      if (this.loading && this.showPatienceHints) {
        this.patienceHintIndex = 0;
      }
    },
    greetingVariants: {
      deep: true,
      handler() {
        if (this.messages.length) return;
        if (!this.hasGreetingVariants) return;
        // Con skipSeedGreeting el padre suele cargar variants async (p. ej. saludo API); hay que sembrar igual.
        this.seedAssistantGreeting();
      },
    },
  },
  mounted() {
    if (!this.skipSeedGreeting) {
      this.seedAssistantGreeting();
    } else if (this.hasGreetingVariants && !this.messages.length) {
      // Variants ya vienen del padre (p. ej. saludo API) al montar.
      this.seedAssistantGreeting();
    }
  },
  beforeDestroy() {
    this.stopPatienceRotation();
  },
  methods: {
    formatTokenCount(n) {
      const v = Number(n) || 0;
      if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
      if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
      return String(v);
    },
    tokenUsageLabel(m) {
      if (!this.isAdminUser) return "";
      const u = m && m.tokenUsage;
      if (!u || !(Number(u.totalTokens) > 0)) return "";
      const key = "ai.supportFABTokenUsage";
      if (typeof this.$tn !== "function") return "";
      const costUsd = Number(u.billableUsd);
      const cost =
        Number.isFinite(costUsd) && costUsd > 0
          ? costUsd < 0.01
            ? costUsd.toFixed(4)
            : costUsd.toFixed(2)
          : "0.00";
      const text = this.$tn(key, {
        total: this.formatTokenCount(u.totalTokens),
        prompt: this.formatTokenCount(u.promptTokens),
        completion: this.formatTokenCount(u.completionTokens),
        calls: Number(u.modelCalls) || 0,
        cost,
      });
      return text === key ? "" : text;
    },
    assistantChoicesVisible(m, idx) {
      return (
        m &&
        m.role === "assistant" &&
        Array.isArray(m.choices) &&
        m.choices.length > 0 &&
        !m.choicesDisabled &&
        !this.disableChoices &&
        !this.busyOverlay
      );
    },
    onChoiceClick(messageIndex, choice) {
      const m = this.messages[messageIndex];
      if (
        !m ||
        m.role !== "assistant" ||
        m.choicesDisabled ||
        this.disableChoices ||
        this.busyOverlay
      ) {
        return;
      }
      this.$set(this.messages[messageIndex], "choicesDisabled", true);
      const label =
        choice && choice.label != null ? String(choice.label).trim() : "";
      const value = choice && choice.value != null ? String(choice.value) : "";
      this.messages = [
        ...this.messages,
        { role: "user", content: label || value },
      ];
      this.$emit("choice", {
        value,
        label: label || value,
        messageIndex,
      });
      this.scrollToBottom();
    },
    /**
     * Mensaje del asistente con opciones clicables debajo.
     * @param {string} content
     * @param {Array<{ value: string, label: string }>} [choices]
     */
    pushAssistant(content, choices) {
      const msg = {
        role: "assistant",
        content: String(content != null ? content : ""),
      };
      if (Array.isArray(choices) && choices.length > 0) {
        msg.choices = choices.map((c) => ({
          value: String(c.value != null ? c.value : ""),
          label: String(c.label != null ? c.label : c.value || ""),
        }));
      }
      this.messages = [...this.messages, msg];
      this.$nextTick(() => this.scrollToBottom());
    },
    pushUserEcho(text) {
      this.messages = [
        ...this.messages,
        {
          role: "user",
          content: String(text != null ? text : ""),
        },
      ];
      this.$nextTick(() => this.scrollToBottom());
    },
    /** Rellena el cuadro de envío sin disparar el servidor (p. ej. recuperar un pedido guardado). */
    prefillDraft(text) {
      this.draft = String(text != null ? text : "");
      this.$nextTick(() => this.scrollToBottom());
    },
    setBusyOverlay(on) {
      this.busyOverlay = !!on;
      this.$nextTick(() => this.scrollToBottom());
    },
    seedAssistantGreeting() {
      const list = (this.greetingVariants || [])
        .map((s) => String(s || "").trim())
        .filter(Boolean);
      if (!list.length) return;
      const pick = list[Math.floor(Math.random() * list.length)];
      this.messages = [{ role: "assistant", content: pick }];
      this.$nextTick(() => this.scrollToBottom());
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.scrollArea;
        if (el && typeof el.scrollTop !== "undefined") {
          el.scrollTop = el.scrollHeight;
        }
      });
    },
    onEnterSend() {
      this.sendFromDraft();
    },
    startPatienceRotation() {
      this.stopPatienceRotation();
      if (!this.showPatienceHints) return;
      const ms = Math.max(5000, Number(this.patienceHintIntervalMs) || 13000);
      this._patienceRotateTimer = setInterval(() => {
        this.patienceHintIndex += 1;
      }, ms);
    },
    stopPatienceRotation() {
      if (this._patienceRotateTimer) {
        clearInterval(this._patienceRotateTimer);
        this._patienceRotateTimer = null;
      }
    },
    clear() {
      this.draft = "";
      this.pendingAttachments = [];
      this.attachmentUploading = false;
      this.lastError = null;
      this.progressText = "";
      this.progressStep = "";
      this.patienceHintIndex = 0;
      this.stopPatienceRotation();
      this.messages = [];
      if (this.hasGreetingVariants) {
        this.seedAssistantGreeting();
      } else if (this.seedAfterClear && !this.skipSeedGreeting) {
        this.seedAssistantGreeting();
      }
      this.$emit("clear");
    },
    buildRequestExtra() {
      const base =
        this.requestBodyExtra && typeof this.requestBodyExtra === "object"
          ? { ...this.requestBodyExtra }
          : {};
      if (!this.allowFileAttachments || !this.pendingAttachments.length) {
        return base;
      }
      const pageContext = {
        ...(base.pageContext && typeof base.pageContext === "object"
          ? base.pageContext
          : {}),
        telegramAttachments: this.pendingAttachments.map((a) => ({ ...a })),
      };
      return { ...base, pageContext };
    },
    triggerAttachmentPick() {
      const el = this.$refs.attachmentInput;
      if (el && typeof el.click === "function") {
        el.value = "";
        el.click();
      }
    },
    removePendingAttachment(index) {
      this.pendingAttachments = this.pendingAttachments.filter(
        (_, i) => i !== index
      );
    },
    async onAttachmentInputChange(ev) {
      const input = ev && ev.target;
      const files = input && input.files ? Array.from(input.files) : [];
      if (!files.length) return;
      const max = Math.max(
        1,
        Number(this.maxPendingAttachments) ||
          SUPPORT_ASSISTANT_MAX_PENDING_ATTACHMENTS
      );
      for (const file of files) {
        if (this.pendingAttachments.length >= max) {
          this.$toast?.error?.(
            this.$tn("ai.supportChatAttachMax", { max })
          );
          break;
        }
        if (file.size > SUPPORT_ASSISTANT_MAX_FILE_BYTES) {
          this.$toast?.error?.(
            this.$tn("File {name} is too large. Maximum size is {mb}MB", {
              name: file.name,
              mb: Math.round(SUPPORT_ASSISTANT_MAX_FILE_BYTES / (1024 * 1024)),
            })
          );
          continue;
        }
        if (!isSupportAssistantAttachmentAllowed(file)) {
          this.$toast?.error?.(
            this.$tn("Support attachment type not allowed", { name: file.name })
          );
          continue;
        }
        try {
          this.attachmentUploading = true;
          const attachment = await uploadAndPrepareSupportAssistantAttachment(
            file
          );
          this.pendingAttachments = [...this.pendingAttachments, attachment];
        } catch (error) {
          console.error("[supportAssistant] attachment upload", error);
          if (isUploadPayloadTooLarge(error)) {
            showSupportUpload413Dialog(this, error);
          } else {
            this.$toast?.error?.(
              error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                this.$tn("ai.supportChatAttachError")
            );
          }
        } finally {
          this.attachmentUploading = false;
        }
      }
      if (input) input.value = "";
    },
    mergeRequestBodyForPost(payloadMessages, userText) {
      const base = this.buildRequestExtra();
      const dyn =
        typeof this.buildRequestBodyExtra === "function"
          ? this.buildRequestBodyExtra({
              messages: payloadMessages,
              userText: String(userText || "").trim(),
            }) || {}
          : {};
      const merged = { ...base, ...dyn };
      if (
        this.allowFileAttachments &&
        this.pendingAttachments.length &&
        merged.pageContext &&
        typeof merged.pageContext === "object"
      ) {
        merged.pageContext = {
          ...merged.pageContext,
          telegramAttachments: this.pendingAttachments.map((a) => ({ ...a })),
        };
      }
      return merged;
    },
    /** Evita mostrar «Network Error» u otros textos crudos de axios al usuario. */
    humanizeTurnRequestFailure(err, rawMessage) {
      const raw =
        rawMessage != null && String(rawMessage).trim()
          ? String(rawMessage).trim()
          : err && err.message
            ? String(err.message).trim()
            : "";
      if (isAxiosTimeoutError(err, raw)) {
        const key = "ai.chatTurnTimeoutSlow";
        return this.$te(key) ? String(this.$tn(key)) : raw || rawMessage;
      }
      if (isAxiosNetworkLikeError(err)) {
        const key = "ai.chatTurnNetworkSlow";
        return this.$te(key) ? String(this.$tn(key)) : raw || rawMessage;
      }
      return rawMessage;
    },
    turnTimeoutUserMessage(hasPartialAssistant) {
      if (hasPartialAssistant) {
        const key = "ai.supportChatTurnTimeoutPartial";
        if (this.$te(key)) return String(this.$tn(key));
      }
      const key = "ai.chatTurnTimeoutSlow";
      return this.$te(key) ? String(this.$tn(key)) : "Tiempo de espera agotado";
    },
    hasPartialAssistantInTurn() {
      return this._turnHadStreamPreview === true;
    },
    scheduleStreamTurnAbort(ac, startedAt) {
      if (this._streamTurnAbortTimer) {
        clearTimeout(this._streamTurnAbortTimer);
        this._streamTurnAbortTimer = null;
      }
      const maxMs = Math.max(15000, Number(this.requestTimeoutMs) || 120000);
      const idleMs =
        Number(this.requestIdleTimeoutMs) > 0
          ? Number(this.requestIdleTimeoutMs)
          : maxMs;
      const elapsed = Date.now() - startedAt;
      const remainingTotal = maxMs - elapsed;
      if (remainingTotal <= 0) {
        this._turnTimedOut = true;
        ac.abort();
        return;
      }
      const delay = this.resetTimeoutOnStreamProgress
        ? Math.min(idleMs, remainingTotal)
        : remainingTotal;
      this._streamTurnAbortTimer = setTimeout(() => {
        this._turnTimedOut = true;
        ac.abort();
      }, delay);
    },
    processTurnPayload(data) {
      if (!data || data.success === false) {
        const err =
          (data && (data.error || data.message)) || "Error en el agente";
        this.lastError = err;
        this.$emit("error", { message: err, data });
        this.messages = this.messages.slice(0, -1);
        return;
      }
      const assistantMessage =
        data.assistantMessage != null ? String(data.assistantMessage) : "";
      const choiceList = Array.isArray(data.assistantChoices)
        ? data.assistantChoices
        : [];
      const normalizedChoices = choiceList
        .filter((c) => c && (c.value != null || c.label != null))
        .map((c) => ({
          value: String(c.value != null ? c.value : ""),
          label: String(c.label != null ? c.label : c.value || ""),
        }));
      let msgs = this.messages;
      if (msgs.length && msgs[msgs.length - 1]._supportPreview) {
        msgs = msgs.slice(0, -1);
      }
      if (assistantMessage || normalizedChoices.length) {
        const msg = {
          role: "assistant",
          content: assistantMessage,
        };
        if (normalizedChoices.length) {
          msg.choices = normalizedChoices;
        }
        const tu = data.tokenUsage;
        if (
          this.isAdminUser &&
          tu &&
          typeof tu === "object" &&
          Number(tu.totalTokens) > 0
        ) {
          msg.tokenUsage = {
            promptTokens: Number(tu.promptTokens) || 0,
            completionTokens: Number(tu.completionTokens) || 0,
            totalTokens: Number(tu.totalTokens) || 0,
            modelCalls: Number(tu.modelCalls ?? tu.calls) || 0,
            billableUsd: Number(tu.billableUsd) || 0,
            currency: tu.currency || "USD",
          };
        }
        msgs = [...msgs, msg];
      }
      this.messages = msgs;
      this.$emit("turn", { ...data, messages: this.messages });
      if (data.phase === "ready" && Array.isArray(data.results)) {
        this.$emit("ready", data);
      }
      this.scrollToBottom();
    },
    sendFromDraft() {
      const text = (this.draft || "").trim();
      if (!this.canSendTurn) return;
      this.draft = "";
      return this.postUserTurn(text);
    },
    /**
     * Envía un mensaje de usuario como si lo hubiera escrito en el campo (p. ej. sugerencia SAIJ).
     */
    submitUserMessage(text) {
      const t = String(text || "").trim();
      if (!t) return Promise.resolve();
      return this.postUserTurn(t);
    },
    abortActiveTurn() {
      if (this._activeTurnAbort) {
        this._turnTimedOut = false;
        this._activeTurnAbort.abort();
      }
    },
    async postUserTurn(userContent) {
      const text = String(userContent || "").trim();
      const attachments =
        this.allowFileAttachments && this.pendingAttachments.length
          ? this.pendingAttachments.map((a) => ({ ...a }))
          : [];
      if ((!text && !attachments.length) || this.loading) return;
      this.lastError = null;
      this.progressText = "";
      this.progressStep = "";
      this.patienceHintIndex = 0;
      const displayContent = buildUserMessageWithAttachments(text, attachments);
      const userMsg = { role: "user", content: displayContent };
      const payloadMessages = [...this.messages, userMsg];
      this.messages = payloadMessages;
      if (
        this.freshWelcomeOnUserGreeting &&
        typeof this.getFreshWelcomeMessage === "function"
      ) {
        const welcome = this.getFreshWelcomeMessage(text);
        if (welcome) {
          this.messages = [
            ...payloadMessages,
            { role: "assistant", content: String(welcome) },
          ];
          this.$emit("turn", {
            success: true,
            assistantMessage: String(welcome),
            clientNavigation: null,
            listingSuggestedAction: null,
            itemSuggestedAction: null,
            tokenUsage: {
              promptTokens: 0,
              completionTokens: 0,
              totalTokens: 0,
              modelCalls: 0,
              billableUsd: 0,
              currency: "USD",
            },
          });
          this.scrollToBottom();
          return;
        }
      }
      if (this.localMode) {
        this.$emit("local-submit", { text, messages: this.messages });
        this.scrollToBottom();
        return;
      }
      if (!this.endpoint || !String(this.endpoint).trim()) {
        this.messages = this.messages.slice(0, -1);
        this.$emit("error", {
          message: "Falta endpoint de chat o activá localMode.",
          data: null,
        });
        return;
      }
      const body = {
        ...this.mergeRequestBodyForPost(payloadMessages, text),
        messages: payloadMessages,
      };
      this.loading = true;
      this.$emit("busy", true);
      this._turnHadStreamPreview = false;
      this.scrollToBottom();
      try {
        if (this.useProgressStream) {
          const ac = new AbortController();
          this._activeTurnAbort = ac;
          this._turnTimedOut = false;
          const tid = setTimeout(() => {
            this._turnTimedOut = true;
            ac.abort();
          }, this.requestTimeoutMs);
          try {
            const isSupportStream = String(this.progressStreamKind || "") === "support";
            let mevSearchPipelineEmitted = false;
            const onStatus = (obj) => {
              if (!isSupportStream) {
                if (
                  !mevSearchPipelineEmitted &&
                  obj &&
                  (obj.step === "mev_agent_preface" || obj.step === "mev_search")
                ) {
                  mevSearchPipelineEmitted = true;
                  this.$emit("mev-search-pipeline-start");
                }
                if (
                  obj &&
                  obj.step === "mev_grid_partial" &&
                  Array.isArray(obj.causes)
                ) {
                  this.$emit("mev-grid-partial", {
                    causes: obj.causes,
                    total: obj.total,
                    multiOrganismo: obj.multiOrganismo,
                    subsetOrganismos: obj.subsetOrganismos,
                    organismoGrupo: obj.organismoGrupo,
                    organismosCompletados: obj.organismosCompletados,
                  });
                }
                if (
                  obj &&
                  (obj.step === "mev_agent_preface" ||
                    obj.step === "mev_organ_progress") &&
                  obj.assistantMessage != null &&
                  String(obj.assistantMessage).trim()
                ) {
                  const text = String(obj.assistantMessage).trim();
                  const last = this.messages[this.messages.length - 1];
                  if (obj.step === "mev_agent_preface") {
                    if (
                      !last ||
                      last.role !== "assistant" ||
                      last.content !== text
                    ) {
                      this.messages = [
                        ...this.messages,
                        { role: "assistant", content: text },
                      ];
                    }
                  } else {
                    this.messages = [
                      ...this.messages,
                      { role: "assistant", content: text },
                    ];
                  }
                }
                this.progressStep =
                  obj && obj.step != null ? String(obj.step).trim() : "";
                this.progressText = formatJurisSearchProgress(
                  this,
                  obj,
                  this.progressKeyPrefix
                );
              } else {
                const previewText =
                  obj &&
                  obj.assistantMessage != null &&
                  String(obj.assistantMessage).trim()
                    ? String(obj.assistantMessage).trim()
                    : "";
                if (previewText) {
                  this._turnHadStreamPreview = true;
                  const last = this.messages[this.messages.length - 1];
                  if (
                    last &&
                    last.role === "assistant" &&
                    last._supportPreview
                  ) {
                    this.$set(this.messages, this.messages.length - 1, {
                      role: "assistant",
                      content: previewText,
                      _supportPreview: true,
                    });
                  } else {
                    this.messages = [
                      ...this.messages,
                      {
                        role: "assistant",
                        content: previewText,
                        _supportPreview: true,
                      },
                    ];
                  }
                  this.progressText = "";
                } else if (obj && obj.step !== "connected") {
                  this.progressStep =
                    obj && obj.step != null ? String(obj.step).trim() : "";
                  this.progressText = formatSupportAssistantProgress(this, obj);
                }
              }
              this.$nextTick(() => this.scrollToBottom());
            };
            const { data } = isSupportStream
              ? await postSupportAssistantNdjson(this.endpoint, body, {
                  signal: ac.signal,
                  onStatus,
                })
              : await postJurisprudenciaNdjson(this.endpoint, body, {
                  signal: ac.signal,
                  onStatus,
                });
            this.processTurnPayload(data);
          } finally {
            if (this._streamTurnAbortTimer) {
              clearTimeout(this._streamTurnAbortTimer);
              this._streamTurnAbortTimer = null;
            }
            this._activeTurnAbort = null;
          }
        } else {
          const { data } = await wencesApi.post(
            this.endpoint,
            body,
            { timeout: this.requestTimeoutMs }
          );
          this.processTurnPayload(data);
        }
      } catch (e) {
        if (e && e.name === "AbortError") {
          const timedOut = this._turnTimedOut === true;
          const partial = timedOut && this.hasPartialAssistantInTurn();
          const timeoutMsg = timedOut
            ? this.turnTimeoutUserMessage(partial)
            : "";
          this.lastError = timedOut ? timeoutMsg : "";
          this.$emit("error", {
            message: timedOut ? timeoutMsg : "Búsqueda detenida",
            original: e,
            userAborted: !timedOut,
            timedOut,
            partialAssistant: partial,
          });
          if (timedOut && !partial) {
            this.messages = this.messages.slice(0, -1);
          }
        } else {
          const raw =
            e &&
            e.response &&
            e.response.data &&
            (e.response.data.error != null || e.response.data.message != null)
              ? String(e.response.data.error || e.response.data.message)
              : e && e.message
                ? e.message
                : "Error de red";
          const msg = this.humanizeTurnRequestFailure(e, raw);
          this.lastError = msg;
          this.$emit("error", { message: msg, original: e });
          this.messages = this.messages.slice(0, -1);
        }
      } finally {
        this.progressText = "";
        this.progressStep = "";
        this._turnTimedOut = false;
        this.loading = false;
        this.$emit("busy", false);
        this.scrollToBottom();
      }
    },
  },
};
</script>

<style scoped>
.agent-choice-chips-wrap .d-flex.flex-wrap {
  max-height: 260px;
  overflow-y: auto;
  padding-bottom: 2px;
}
.agent-choice-chip {
  border: 1px solid rgba(13, 110, 253, 0.45);
  background: #fff;
  color: #0d6efd;
  font-weight: 500;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
}
.agent-choice-chip:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 10px rgba(13, 110, 253, 0.18);
  background: rgba(13, 110, 253, 0.07);
}
.agent-choice-chip:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.agent-thinking-dots {
  display: inline-block;
  margin-left: 2px;
  white-space: nowrap;
  letter-spacing: 1px;
}
.agent-thinking-dot {
  display: inline-block;
  animation: agentThinkingDotPulse 1.2s ease-in-out infinite;
  opacity: 0.25;
}
.agent-thinking-dot:nth-child(2) {
  animation-delay: 0.2s;
}
.agent-thinking-dot:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes agentThinkingDotPulse {
  0%,
  100% {
    opacity: 0.2;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-1px);
  }
}
</style>
